import React, { useState } from 'react';
import { Heart } from 'lucide-react';

interface LoveMessagesSectionProps {
  invitationId: string | null;
  isLive: boolean;
  language: 'en' | 'mr';
  onMessageAdded: () => void;
  totalMessages: number;
}

export const LoveMessagesSection: React.FC<LoveMessagesSectionProps> = ({
  language,
  onMessageAdded,
  totalMessages,
}) => {
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [messages, setMessages] = useState<Array<{ name: string; message: string; likes: number }>>([]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) return;

    setMessages([...messages, { name, message, likes: 0 }]);
    setName('');
    setMessage('');
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 2000);
    onMessageAdded();
  };

  const toggleLike = (idx: number) => {
    const updated = [...messages];
    updated[idx].likes = (updated[idx].likes + 1) % 2;
    setMessages(updated);
  };

  return (
    <section className="relative py-24 px-4 bg-gradient-to-b from-transparent via-rose-900/10 to-transparent">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-center text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-rose-400 mb-12">
          {language === 'mr' ? '💌 प्रेमाचे संदेश' : '💌 Love Messages'}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <form onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-md rounded-2xl border-2 border-amber-400/30 p-8 shadow-2xl">
              <h3 className="text-xl font-bold text-amber-100 mb-4">
                {language === 'mr' ? 'आपला संदेश पाठवा' : 'Send Your Message'}
              </h3>

              <div className="mb-4">
                <label className="block text-sm font-bold text-rose-200 mb-2">
                  {language === 'mr' ? 'तुमचे नाव' : 'Your Name'}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full bg-white/10 border-2 border-amber-400/30 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:border-rose-400 focus:outline-none"
                  placeholder={language === 'mr' ? 'नाव' : 'Name'}
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-bold text-rose-200 mb-2">
                  {language === 'mr' ? 'प्रेमाचा संदेश' : 'Your Message'}
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={4}
                  className="w-full bg-white/10 border-2 border-amber-400/30 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:border-rose-400 focus:outline-none resize-none"
                  placeholder={language === 'mr' ? 'आपला संदेश' : 'Share your blessings...'}
                />
              </div>

              {isSubmitted && (
                <div className="mb-4 p-4 bg-rose-500/20 border border-rose-400 rounded-lg text-rose-200 text-center">
                  ✨ {language === 'mr' ? 'धन्यवाद!' : 'Thank you!'}
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-amber-500 to-rose-500 text-white font-bold py-3 rounded-lg hover:shadow-lg hover:shadow-rose-500/50 transition-all"
              >
                💌 {language === 'mr' ? 'पाठवा' : 'Send'}
              </button>
            </form>
          </div>

          <div>
            <h3 className="text-xl font-bold text-amber-100 mb-4">
              {language === 'mr' ? 'संदेश' : 'Messages'} ({messages.length + totalMessages})
            </h3>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {messages.map((msg, idx) => (
                <div key={idx} className="bg-white/5 backdrop-blur-sm rounded-xl border border-amber-400/20 p-4 hover:border-rose-400/50 transition-all">
                  <div className="flex justify-between items-start mb-2">
                    <p className="font-bold text-amber-200">{msg.name}</p>
                    <button
                      onClick={() => toggleLike(idx)}
                      className="text-red-400 hover:scale-125 transition-transform"
                    >
                      {msg.likes > 0 ? '❤️' : '🤍'}
                    </button>
                  </div>
                  <p className="text-gray-300 text-sm mb-2">{msg.message}</p>
                  <p className="text-xs text-gray-500">
                    {msg.likes} {language === 'mr' ? 'प्रेम' : 'love'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
